import React from "react";
import { InlineMath, BlockMath } from "react-katex";

import MaturaFunctionGraph from "../../../../components/GraphPlotter/MaturaFunctionGraph";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function graphEquation(a, p, q) {
  return `y=${a}*(x-(${p}))^2+(${q})`;
}

function generateParams() {
  // Budujemy parabolę z rzeczywistymi miejscami zerowymi:
  // f(x)=a(x-p)^2+q, gdzie q = -a*r^2.
  // Dzięki temu:
  // - przy q>0 ramiona są w dół,
  // - przy q<0 ramiona są w górę,
  // - miejsca zerowe istnieją zawsze (x = p ± r).
  const combos = [
    { r: 1, aAbs: 1 },
    { r: 1, aAbs: 2 },
    { r: 2, aAbs: 1 },
  ];

  for (let i = 0; i < 500; i += 1) {
    const p = pick([-3, -2, -1, 1, 2, 3]);
    const { r, aAbs } = pick(combos);
    const up = Math.random() < 0.5;

    const a = (up ? 1 : -1) * aAbs;
    const q = -a * r * r;

    const x1 = p - r;
    const x2 = p + r;
    if (x1 < -5 || x2 > 5) continue;

    // Zapewniamy sensowną widoczność fragmentu wykresu.
    const inRangeCount = Array.from({ length: 11 }, (_, idx) => idx - 5)
      .map((x) => a * (x - p) * (x - p) + q)
      .filter((y) => y >= -5 && y <= 5).length;

    if (inRangeCount < 6) continue;
    return { a, p, q };
  }

  return { a: -1, p: 1, q: 4 };
}

function buildOrderedOptions(p, q) {
  const xOptions = shuffle([
    { axis: "x", value: p, isCorrect: true },
    { axis: "x", value: -p, isCorrect: false },
  ]);

  const yOptions = shuffle([
    { axis: "y", value: q, isCorrect: false },
    { axis: "y", value: -q, isCorrect: false },
  ]);

  const grouped = Math.random() < 0.5
    ? [...xOptions, ...yOptions] // XXYY
    : [...yOptions, ...xOptions]; // YYXX

  return grouped.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
  }));
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaQuadraticAxisFromGraphClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: "12.2",
    },
    data: {
      a: -1 / 3,
      p: 3,
      q: 6,
      options: [
        { label: "A", axis: "x", value: 3, isCorrect: true },
        { label: "B", axis: "x", value: -3, isCorrect: false },
        { label: "C", axis: "y", value: 6, isCorrect: false },
        { label: "D", axis: "y", value: -6, isCorrect: false },
      ],
      correctIndex: 0,
      xRange: [-3, 6],
      yRange: [-2, 7],
      maxPoints: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  const { a, p, q } = generateParams();
  const options = buildOrderedOptions(p, q);
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaQuadraticAxisFromGraphClosedTask",
    data: {
      a,
      p,
      q,
      options,
      correctIndex,
      xRange: [-5, 5],
      yRange: [-5, 5],
    },
  };
}

export function renderTask({ data }) {
  const { a, p, q, options, xRange, yRange } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte.</strong>
      </p>
      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math={"(x,y)"} />{" "}
        przedstawiono fragment paraboli, która jest wykresem funkcji kwadratowej{" "}
        <InlineMath math={"f"} />.
      </p>
      <p>
        Wierzchołek tej paraboli ma współrzędne{" "}
        <InlineMath math={`(p,q)=(${p},\\;${q})`} />.
      </p>

      <div style={{ margin: "0 auto", maxWidth: 350 }}>
        <MaturaFunctionGraph
          width={350}
          equations={[graphEquation(a, p, q)]}
          xRange={xRange}
          yRange={yRange}
        />
      </div>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Osią symetrii wykresu funkcji <InlineMath math={"f"} /> jest prosta o
        równaniu:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 6 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.axis} = ${opt.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      axis: opt.axis,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return (
    <p style={{ textAlign: "left" }}>
      <InlineMath
        math={`${correct.label}.\\; ${correct.axis} = ${correct.value}`}
      />
    </p>
  );
}

export function renderExplanation({ data }) {
  const { p, q } = data;
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Oś symetrii paraboli przechodzi przez
        wierzchołek i jest pionowa.
      </p>
      <p>
        Dla wierzchołka <InlineMath math={`(p,q)=(${p},${q})`} /> dostajemy:
      </p>
      <BlockMath math={`x=${p}`} />
      <p>
        Równania <InlineMath math={`y=${q}`} /> i{" "}
        <InlineMath math={`y=${-q}`} /> opisują proste poziome, więc nie są osią
        symetrii tej paraboli.
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    typeof userAnswer.axis !== "string"
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath
        math={`${userAnswer.label}.\\; ${userAnswer.axis} = ${userAnswer.value}`}
      />
    </div>
  );
}
